/**
 * Client helpers for GET /api/billing/rates — single source of truth for displayed pricing.
 * Rate values are never hardcoded; fetch from the API before computing or formatting costs.
 */

export interface QueryTier {
  up_to: number
  units_per_1000: number
}

export interface TransferTier {
  up_to_mb: number
  dollars_per_100_mb: number
}

export interface BillingUnit {
  abbreviated: string
  long: string
  description: string
}

export interface BillingMeter {
  id: string
  label: string
  description: string
  consumption: BillingUnit
  rate: BillingUnit
}

export interface BillingRates {
  cpu_dollars_per_millicore_hour: number
  mem_dollars_per_mib_hour: number
  cpu_dollars_per_core_hour?: number
  mem_dollars_per_gib_hour?: number
  millicores_per_cpu_unit: number
  mib_per_mem_unit: number
  gateway_queries_per_billing_unit?: number
  query_tiers?: QueryTier[]
  fork_query_tiers?: QueryTier[]
  transfer_tiers?: TransferTier[]
  storage_dollars_per_mib_hour?: number
  storage_dollars_per_gib_hour?: number
  seed_snapshot_storage_dollars_per_gib_month?: number
  transfer_dollars_per_gib?: number
  fork_transfer_dollars_per_gib?: number
  ebs_storage_upcharge_percent?: number
  meters?: BillingMeter[]
}

let activeRates: BillingRates | null = null
const ratesByPeriod = new Map<string, BillingRates>()

export function isValidBillingRates(r: BillingRates | null): r is BillingRates {
  return (
    r != null
    && r.cpu_dollars_per_millicore_hour > 0
    && r.mem_dollars_per_mib_hour > 0
    && r.millicores_per_cpu_unit > 0
    && r.mib_per_mem_unit > 0
  )
}

function periodCacheKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

function periodCacheKeyFromDate(d: Date): string {
  return periodCacheKey(d.getUTCFullYear(), d.getUTCMonth() + 1)
}

function resolveRates(periodStart?: Date): BillingRates | null {
  if (periodStart) {
    const cached = ratesByPeriod.get(periodCacheKeyFromDate(periodStart))
    if (cached) return cached
  }
  return activeRates
}

export function billingRatesReady(): boolean {
  return isValidBillingRates(activeRates)
}

export function billingPeriodStartUtc(year: number, month: number): Date {
  return new Date(Date.UTC(year, month - 1, 1))
}

export function billingPeriodStartFromIso(iso: string): Date {
  const d = new Date(iso)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
}

export function applyBillingRates(rates: BillingRates, periodStart?: Date): void {
  activeRates = rates
  if (periodStart) {
    ratesByPeriod.set(periodCacheKeyFromDate(periodStart), rates)
    return
  }
  const now = new Date()
  ratesByPeriod.set(periodCacheKey(now.getUTCFullYear(), now.getUTCMonth() + 1), rates)
}

export function getBillingRates(): BillingRates | null {
  return activeRates
}

export function requireBillingRates(): BillingRates {
  if (!isValidBillingRates(activeRates)) {
    throw new Error('Billing rates not loaded')
  }
  return activeRates
}

/** Fetch canonical rates from the billing API. */
export async function fetchBillingRates(
  apiUrl: string,
  year?: number,
  month?: number,
): Promise<boolean> {
  const data = await fetchBillingRatesData(apiUrl, year, month)
  return data != null
}

/** Fetch rates and return the parsed payload (for region-scoped UI state). */
export async function fetchBillingRatesData(
  apiUrl: string,
  year?: number,
  month?: number,
): Promise<BillingRates | null> {
  try {
    const base = apiUrl.replace(/\/+$/, '')
    const params = year != null && month != null ? `?year=${year}&month=${month}` : ''
    const res = await fetch(`${base}/api/billing/rates${params}`)
    if (!res.ok) return null
    const data = (await res.json()) as BillingRates
    if (!isValidBillingRates(data)) return null
    if (year != null && month != null) {
      ratesByPeriod.set(periodCacheKey(year, month), data)
    } else {
      const now = new Date()
      ratesByPeriod.set(periodCacheKey(now.getUTCFullYear(), now.getUTCMonth() + 1), data)
    }
    activeRates = data
    return data
  } catch {
    return null
  }
}

export function cpuUnitsToMillicoreHours(cpuUnits: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates) || cpuUnits <= 0) return 0
  return (cpuUnits * rates.millicores_per_cpu_unit) / 60
}

export function memUnitsToMibHours(memUnits: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates) || memUnits <= 0) return 0
  return (memUnits * rates.mib_per_mem_unit) / 60
}

export function cpuCostFromMillicoreHours(mch: number, rates?: BillingRates | null, periodStart?: Date): number {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r) || mch <= 0) return 0
  return mch * r.cpu_dollars_per_millicore_hour
}

export function memCostFromMibHours(mibH: number, rates?: BillingRates | null, periodStart?: Date): number {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r) || mibH <= 0) return 0
  return mibH * r.mem_dollars_per_mib_hour
}

export function storageCostFromMibHours(mibH: number, rates?: BillingRates | null, periodStart?: Date): number {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r) || mibH <= 0) return 0
  const rate = r.storage_dollars_per_mib_hour
  if (rate == null || rate <= 0) return 0
  return mibH * rate
}

export function computeCpuCharge(cpuUnits: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates)) return 0
  return cpuUnitsToMillicoreHours(cpuUnits, periodStart) * rates.cpu_dollars_per_millicore_hour
}

export function computeMemCharge(memUnits: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates)) return 0
  return memUnitsToMibHours(memUnits, periodStart) * rates.mem_dollars_per_mib_hour
}

export function computeQueryBillingUnits(queryCount: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates) || queryCount <= 0) return 0
  return computeQueryBillingUnitsWithTiers(queryCount, rates.query_tiers ?? [])
}

export function computeForkQueryBillingUnits(queryCount: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates) || queryCount <= 0) return 0
  const tiers = rates.fork_query_tiers ?? []
  if (tiers.length === 0) {
    // Fallback: half of filtered-gateway units if fork tiers missing from older rate payloads.
    return computeQueryBillingUnits(queryCount, periodStart) * 0.5
  }
  return computeQueryBillingUnitsWithTiers(queryCount, tiers)
}

function computeQueryBillingUnitsWithTiers(queryCount: number, tiers: QueryTier[]): number {
  let units = 0
  let prevCap = 0
  for (const tier of tiers) {
    const inTier = Math.max(0, Math.min(queryCount, tier.up_to) - prevCap)
    if (inTier > 0) {
      units += (inTier / 1000) * tier.units_per_1000
    }
    prevCap = tier.up_to
    if (queryCount <= tier.up_to) break
  }
  return units
}

export function incrementalQueryBillingUnits(
  queriesBefore: number,
  queryCount: number,
  periodStart?: Date,
): number {
  if (queryCount <= 0) return 0
  return computeQueryBillingUnits(queriesBefore + queryCount, periodStart)
    - computeQueryBillingUnits(queriesBefore, periodStart)
}

export function computeHostedQueryCharge(queryCount: number, periodStart?: Date): number {
  return computeCpuCharge(computeQueryBillingUnits(queryCount, periodStart), periodStart)
}

export function computeHostedForkQueryCharge(queryCount: number, periodStart?: Date): number {
  return computeCpuCharge(computeForkQueryBillingUnits(queryCount, periodStart), periodStart)
}

export function incrementalHostedQueryCharge(
  queriesBefore: number,
  queryCount: number,
  periodStart?: Date,
): number {
  if (queryCount <= 0) return 0
  return computeCpuCharge(incrementalQueryBillingUnits(queriesBefore, queryCount, periodStart), periodStart)
}

export function incrementalForkQueryBillingUnits(
  queriesBefore: number,
  queryCount: number,
  periodStart?: Date,
): number {
  if (queryCount <= 0) return 0
  return computeForkQueryBillingUnits(queriesBefore + queryCount, periodStart)
    - computeForkQueryBillingUnits(queriesBefore, periodStart)
}

export function incrementalHostedForkQueryCharge(
  queriesBefore: number,
  queryCount: number,
  periodStart?: Date,
): number {
  if (queryCount <= 0) return 0
  return computeCpuCharge(incrementalForkQueryBillingUnits(queriesBefore, queryCount, periodStart), periodStart)
}

export function dailyHostedQueryCostUsd(
  days: { date: string; query_count: number }[],
  periodStart?: Date,
): Map<string, number> {
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date))
  const out = new Map<string, number>()
  let queriesBefore = 0
  for (const day of sorted) {
    const cost = incrementalHostedQueryCharge(queriesBefore, day.query_count, periodStart)
    if (day.query_count > 0) {
      out.set(day.date, Math.round(cost * 100) / 100)
    }
    queriesBefore += day.query_count
  }
  return out
}

export function dailyHostedForkQueryCostUsd(
  days: { date: string; query_count: number }[],
  periodStart?: Date,
): Map<string, number> {
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date))
  const out = new Map<string, number>()
  let queriesBefore = 0
  for (const day of sorted) {
    const cost = incrementalHostedForkQueryCharge(queriesBefore, day.query_count, periodStart)
    if (day.query_count > 0) {
      out.set(day.date, Math.round(cost * 100) / 100)
    }
    queriesBefore += day.query_count
  }
  return out
}

export function computeHostedDataTransferCharge(bytes: number, periodStart?: Date): number {
  if (bytes <= 0) return 0
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates)) return 0
  const flat = rates.transfer_dollars_per_gib
  if (flat != null && flat > 0) {
    return (bytes / (1024 * 1024 * 1024)) * flat
  }
  const mb = bytes / (1024 * 1024)
  const tiers = rates.transfer_tiers ?? []
  let charge = 0
  let prevCap = 0
  for (const tier of tiers) {
    const inTier = Math.max(0, Math.min(mb, tier.up_to_mb) - prevCap)
    if (inTier > 0) {
      charge += (inTier / 100) * tier.dollars_per_100_mb
    }
    prevCap = tier.up_to_mb
    if (mb <= tier.up_to_mb) break
  }
  return charge
}

export function computeHostedForkDataTransferCharge(bytes: number, periodStart?: Date): number {
  if (bytes <= 0) return 0
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates)) return 0
  const flat = rates.fork_transfer_dollars_per_gib
  if (flat != null && flat > 0) {
    return (bytes / (1024 * 1024 * 1024)) * flat
  }
  // Fallback: half of gateway transfer if fork rate missing.
  return computeHostedDataTransferCharge(bytes, periodStart) * 0.5
}

export function formatDataTransferBytes(bytes: number): string {
  if (bytes <= 0) return '0 B'
  const mb = bytes / (1024 * 1024)
  if (mb < 1) {
    const kb = bytes / 1024
    return `${kb.toLocaleString('en-US', { maximumFractionDigits: 1 })} KB`
  }
  if (mb < 1024) {
    return `${mb.toLocaleString('en-US', { maximumFractionDigits: 2 })} MB`
  }
  const gb = mb / 1024
  return `${gb.toLocaleString('en-US', { maximumFractionDigits: 2 })} GB`
}

export function dollarsPerBillingUnit(periodStart?: Date): number {
  return computeCpuCharge(1, periodStart)
}

export function queryCountToBillingUnits(queryCount: number, periodStart?: Date): number {
  const rates = resolveRates(periodStart)
  const perUnit = rates?.gateway_queries_per_billing_unit
  if (queryCount <= 0 || !perUnit || perUnit <= 0) return 0
  return queryCount / perUnit
}

export function dailyQueryBillingUnits(
  days: { date: string; query_count: number; billing_units?: number }[],
  periodStart?: Date,
): Map<string, number> {
  const out = new Map<string, number>()
  for (const day of days) {
    if (day.query_count <= 0) continue
    out.set(
      day.date,
      day.billing_units ?? queryCountToBillingUnits(day.query_count, periodStart),
    )
  }
  return out
}

export function billableWorkCost(
  work: { cpu_units: number; mem_units: number },
  periodStart?: Date,
): number {
  return computeCpuCharge(work.cpu_units, periodStart) + computeMemCharge(work.mem_units, periodStart)
}

export function computeHostedUsageCharges(
  row: {
    cpu_units: number
    mem_units: number
    query_count_hosted?: number
    query_count_fork?: number
  },
  periodStart?: Date,
): { cpu: number; memory: number; queries: number; forkQueries: number; total: number } {
  const cpu = computeCpuCharge(row.cpu_units, periodStart)
  const memory = computeMemCharge(row.mem_units, periodStart)
  const queries = computeHostedQueryCharge(row.query_count_hosted ?? 0, periodStart)
  const forkQueries = computeHostedForkQueryCharge(row.query_count_fork ?? 0, periodStart)
  return { cpu, memory, queries, forkQueries, total: cpu + memory + queries + forkQueries }
}

export function estimateMonthlyHostedCharges(
  monthToDateUsd: number,
  startDate: string,
  endDate: string,
  now: Date = new Date(),
): number {
  if (monthToDateUsd <= 0) return 0

  const start = new Date(`${startDate}T00:00:00Z`)
  const end = new Date(`${endDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return monthToDateUsd
  }

  const msPerDay = 86_400_000
  const periodDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / msPerDay) + 1)
  const asOf = now.getTime() > end.getTime() ? end : now
  const elapsedDays = Math.max(1, Math.min(periodDays, Math.round((asOf.getTime() - start.getTime()) / msPerDay) + 1))

  if (elapsedDays >= periodDays) return monthToDateUsd
  return monthToDateUsd * (periodDays / elapsedDays)
}

function billingUnitUsd(rates: BillingRates): number {
  const mcpuH = rates.millicores_per_cpu_unit / 60
  return mcpuH * rates.cpu_dollars_per_millicore_hour
}

export function getBillingMeter(
  id: string,
  rates?: BillingRates | null,
): BillingMeter | undefined {
  const r = rates ?? activeRates
  return r?.meters?.find((m) => m.id === id)
}

export function formatConsumptionAmount(
  amount: number,
  meterId: string,
  options?: { long?: boolean; rates?: BillingRates | null },
): string {
  const meter = getBillingMeter(meterId, options?.rates)
  const unit = options?.long ? meter?.consumption.long : meter?.consumption.abbreviated
  if (amount <= 0) return '—'
  const formatted = amount.toLocaleString('en-US', { maximumFractionDigits: 3 })
  return unit ? `${formatted} ${unit}` : formatted
}

export function formatRatePerUnit(meterId: string, rates?: BillingRates | null): string {
  const meter = getBillingMeter(meterId, rates)
  return meter?.rate.long ? `per ${meter.rate.long}` : ''
}

export function formatUnitRate(rate: number): string {
  if (rate <= 0) return '—'
  if (rate >= 0.01) return `$${rate.toFixed(2)}`
  const decimals = Math.min(8, Math.max(2, Math.ceil(-Math.log10(rate)) + 1))
  return `$${rate.toFixed(decimals)}`
}

function cpuRatePerCoreHour(r: BillingRates): number {
  return r.cpu_dollars_per_core_hour ?? r.cpu_dollars_per_millicore_hour * 1000
}

function memRatePerGibHour(r: BillingRates): number {
  return r.mem_dollars_per_gib_hour ?? r.mem_dollars_per_mib_hour * 1024
}

function storageRatePerGibHour(r: BillingRates): number | null {
  if (r.storage_dollars_per_gib_hour != null && r.storage_dollars_per_gib_hour > 0) {
    return r.storage_dollars_per_gib_hour
  }
  const mib = r.storage_dollars_per_mib_hour
  if (mib == null || mib <= 0) return null
  return mib * 1024
}

export function formatCpuRate(rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  if (!isValidBillingRates(r)) return '—'
  const per = formatRatePerUnit('cpu', r)
  return `${formatUnitRate(cpuRatePerCoreHour(r))}${per ? ` ${per}` : ''}`
}

export function formatMemRate(rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  if (!isValidBillingRates(r)) return '—'
  const per = formatRatePerUnit('memory', r)
  return `${formatUnitRate(memRatePerGibHour(r))}${per ? ` ${per}` : ''}`
}

export function formatStorageRate(rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  if (!isValidBillingRates(r)) return '—'
  const rate = storageRatePerGibHour(r)
  if (rate == null || rate <= 0) return '—'
  const per = formatRatePerUnit('storage', r)
  return `${formatUnitRate(rate)}${per ? ` ${per}` : ''}`
}

export function formatSeedSnapshotStorageRate(rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  if (!isValidBillingRates(r)) return '—'
  const rate = r.seed_snapshot_storage_dollars_per_gib_month
  if (rate == null || rate <= 0) return '—'
  const per = formatRatePerUnit('seed_snapshot_storage', r)
  return `${formatUnitRate(rate)}${per ? ` ${per}` : ''}`
}

export function computeSeedSnapshotStorageCharge(
  bytes: number,
  periodStart: Date,
  periodEnd?: Date,
): number {
  if (bytes <= 0) return 0
  const rates = resolveRates(periodStart)
  if (!isValidBillingRates(rates)) return 0
  const rate = rates.seed_snapshot_storage_dollars_per_gib_month
  if (rate == null || rate <= 0) return 0
  const end = periodEnd ?? new Date()
  const hours = Math.max(0, (end.getTime() - periodStart.getTime()) / (1000 * 60 * 60))
  const billableHours = Math.min(hours, 730)
  if (billableHours <= 0) return 0
  const gib = bytes / (1024 * 1024 * 1024)
  return gib * rate * (billableHours / 730)
}

export function formatTransferStartingRate(rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  const flat = r?.transfer_dollars_per_gib
  const meter = getBillingMeter('database_traffic', r)
  const unit = meter?.rate.abbreviated ?? 'GB'
  if (flat != null && flat > 0) {
    return `$${flat.toFixed(2)}/${unit}`
  }
  const tier = r?.transfer_tiers?.[0]
  if (!tier) return '—'
  return `$${tier.dollars_per_100_mb.toFixed(2)}/100 MB`
}

export function formatQueryStartingRate(rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  if (!isValidBillingRates(r)) return '—'
  const usd = computeQueryBillingUnits(1000) * billingUnitUsd(r)
  const meter = getBillingMeter('database_queries', r)
  const unit = meter?.rate.long ?? '1,000 queries'
  if (usd >= 0.01) return `$${usd.toFixed(2)}/${unit}`
  if (usd >= 0.001) return `$${usd.toFixed(3)}/${unit}`
  return `$${usd.toFixed(4)}/${unit}`
}

export function formatCpuCostFormula(mch: number, rates?: BillingRates | null, periodStart?: Date): string {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r)) return '—'
  const coreH = mch / 1000
  return `${coreH.toLocaleString('en-US')} × ${formatUnitRate(cpuRatePerCoreHour(r))}`
}

export function formatMemCostFormula(mibH: number, rates?: BillingRates | null, periodStart?: Date): string {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r)) return '—'
  const gibH = mibH / 1024
  return `${gibH.toLocaleString('en-US', { maximumFractionDigits: 3 })} × ${formatUnitRate(memRatePerGibHour(r))}`
}

export function formatStorageCostFormula(mibH: number, rates?: BillingRates | null, periodStart?: Date): string {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r)) return '—'
  const rate = storageRatePerGibHour(r)
  if (rate == null || rate <= 0) return '—'
  const gibH = mibH / 1024
  return `${gibH.toLocaleString('en-US', { maximumFractionDigits: 3 })} × ${formatUnitRate(rate)}`
}

export function formatTransferCostFormula(bytes: number, rates?: BillingRates | null): string {
  const r = rates ?? activeRates
  if (bytes <= 0) return '—'
  const gib = bytes / (1024 * 1024 * 1024)
  const flat = r?.transfer_dollars_per_gib
  const meter = getBillingMeter('database_traffic', r)
  const unit = meter?.rate.abbreviated ?? 'GB'
  if (flat != null && flat > 0) {
    return `${gib.toLocaleString('en-US', { maximumFractionDigits: 3 })} ${unit} × $${flat.toFixed(2)}/${unit}`
  }
  const tier = r?.transfer_tiers?.[0]
  if (!tier) return '—'
  const mb = bytes / (1024 * 1024)
  if (mb < 1024) {
    return `${mb.toLocaleString('en-US', { maximumFractionDigits: 2 })} MB × $${tier.dollars_per_100_mb.toFixed(2)}/100 MB`
  }
  const gb = mb / 1024
  return `${gb.toLocaleString('en-US', { maximumFractionDigits: 2 })} GB × $${tier.dollars_per_100_mb.toFixed(2)}/100 MB`
}

export function formatQueryCostFormula(queryCount: number, rates?: BillingRates | null, periodStart?: Date): string {
  const r = rates ?? resolveRates(periodStart)
  if (!isValidBillingRates(r) || queryCount <= 0) return '—'
  const usd = computeQueryBillingUnits(1000, periodStart) * billingUnitUsd(r)
  const meter = getBillingMeter('database_queries', r)
  const unit = meter?.rate.abbreviated ?? '1k queries'
  const rateLabel = usd >= 0.01
    ? `$${usd.toFixed(2)}`
    : usd >= 0.001
      ? `$${usd.toFixed(3)}`
      : `$${usd.toFixed(4)}`
  return `${queryCount.toLocaleString('en-US')} × ${rateLabel}/${unit}`
}

export function formatUsd(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function formatUsdPrecise(amount: number): string {
  const maxDigits = amount !== 0 && Math.abs(amount) < 0.01 ? 4 : 2
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: maxDigits,
  })
}

export function formatUsdFlexible(amount: number): string {
  if (amount <= 0) return '$0.00'
  if (amount >= 0.01) {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  if (amount >= 0.001) {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })
  }
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })
}
